import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Navigate, Route, Routes, useLocation, useParams } from 'react-router-dom';
import Sidebar from './components/Sidebar';
import Dashboard from './components/Dashboard/Dashboard';
import AddProject from './components/AddProject';
import LoadingScreen from './components/LoadingScreen';
import GetCase from './components/GetCase';
import CallbackOutlookApiPage from './components/CallbackOutlookApi';
import GlobalSetting from './components/GlobalSetting';
import LoginPage from './components/LoginPage';
import SaasAccessPage from './components/SaasAccessPage';
import OrganizationWorkspace from './components/OrganizationWorkspace';
import OrganizationWorkspaceNavigation from './components/OrganizationWorkspaceNavigation';
import SaasPlatformConsole from './components/SaasPlatformConsole';
import AuthRoute from './components/AuthRoute';
import ProtectedRoute from './components/ProtectedRoute';
import ReadOnlyProjectDashboard from './components/ReadOnlyProjectDashboard';
import ValidatorWorkspace from './components/ValidatorWorkspace';
import { DashboardProvider } from './components/Dashboard/DashboardContext';
import { Box } from '@mui/material';
import { useWebSocket } from './components/WebSocketContext';
import { useUser } from './components/WebSocketContext';
import DisconnectedOverlay from './components/DisconnectOverlay';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import StatusBar from './components/StatusBar';
import { DashboardErrorStatusProvider } from './components/DashboardErrorStatusContext';
import SoftwareCanBeUpdated from './components/SoftwareCanBeUpdated';
import MfaSetupDialog from './components/MfaSetupDialog';
import PurchaseCredits from './components/PurchaseCredits';
import ConnectorCreatorProvider from './components/ConnectorCreatorProvider';
import { useTranslation } from 'react-i18next';
import {
  clearCaseCreation,
  isCaseCreationManagementRoute,
  subscribeToCaseCreationState
} from './Utils/caseCreationState';
import { getProjectNavigationPath } from './Utils/projectNavigation';
import { RememberProjectRoute, WorkspaceHomeRoute } from './components/ResumeNavigation';
import { isSaasOrganizationSelected } from './Utils/saasOrganization';

function AddProjectRoute() {
  const location = useLocation();
  const { organizationId, organizations } = useUser();
  const onboarding = new URLSearchParams(location.search).get('onboarding') === 'true';
  return onboarding && isSaasOrganizationSelected(organizationId, organizations)
    ? <OrganizationWorkspace welcome /> : <AddProject />;
}

function HomeRoute() {
  return <WorkspaceHomeRoute />;
}

function RouteAccessModeSync() {
  const location = useLocation();
  const { setPublicRouteActive } = useWebSocket();

  useEffect(() => {
    setPublicRouteActive(location.pathname.startsWith('/public/case/'));
  }, [location.pathname, setPublicRouteActive]);

  return null;
}

function ValidatorCaseRoute() {
  const { isValidator } = useUser();
  const { caseid } = useParams();

  // Validators must use the validation workspace for every project entry point.
  // ValidatorWorkspace owns the active-lock check and redirects to an already
  // active project when necessary.
  if (isValidator) {
    return <Navigate to={getProjectNavigationPath(caseid, true)} replace />;
  }

  return <Dashboard />;
}

function ValidatorProjectDashboardRoute() {
  const { isValidator } = useUser();

  if (isValidator) {
    return <Navigate to="/validate" replace />;
  }

  return <ReadOnlyProjectDashboard />;
}

function SidebarForRoute() {
  return <Sidebar />;
}

function ValidatorWorkspaceRoute() {
  const { isValidator } = useUser();

  if (!isValidator) {
    return <Navigate to="/" replace />;
  }

  return (
    <DashboardProvider>
      <ValidatorWorkspace />
    </DashboardProvider>
  );
}

function PurchaseCreditsRoute() {
  const { isConnected, sendMessage, subscribeToMessages } = useWebSocket();
  const [isPaymentsEnabled, setIsPaymentsEnabled] = useState(null);

  useEffect(() => {
    const unsubscribe = subscribeToMessages((parsedMessage) => {
      if (parsedMessage.command !== 'configuration_data') {
        return;
      }

      const payload = parsedMessage.data || {};
      if (payload.paymentsEnabled !== undefined || payload.tokenPurchasable !== undefined) {
        setIsPaymentsEnabled(Boolean(payload.paymentsEnabled ?? payload.tokenPurchasable));
      }
    });

    return () => unsubscribe();
  }, [subscribeToMessages]);

  useEffect(() => {
    if (!isConnected || typeof sendMessage !== 'function') {
      return;
    }
    sendMessage({ command: 'get_performance_settings' });
  }, [isConnected, sendMessage]);

  if (isPaymentsEnabled === false) {
    return <Navigate to="/Settings" replace />;
  }

  if (isPaymentsEnabled === null) {
    return null;
  }

  return <PurchaseCredits />;
}

export function CaseCreationOverlay() {
  const location = useLocation();
  const { t } = useTranslation();
  const [isCreatingProject, setIsCreatingProject] = React.useState(false);

  useEffect(() => subscribeToCaseCreationState(setIsCreatingProject), []);

  const pathname = String(location.pathname || '');
  const isManagementRoute = isCaseCreationManagementRoute(pathname);

  useEffect(() => {
    if (isCreatingProject && isManagementRoute) {
      clearCaseCreation();
    }
  }, [isCreatingProject, isManagementRoute]);

  const hideOverlay =
    pathname.startsWith('/case/') ||
    pathname.startsWith('/public/case/') ||
    pathname === '/login' ||
    pathname === '/welcome' ||
    (pathname === '/add' && new URLSearchParams(location.search).get('onboarding') === 'true') ||
    isManagementRoute;

  if (!isCreatingProject || hideOverlay) {
    return null;
  }

  return (
    <Box
      sx={{
        position: 'fixed',
        inset: 0,
        zIndex: 2000,
      }}
    >
      <LoadingScreen
        status="updating"
        updateMessage={t('addcase.creating_project', 'Creating your project...')}
      />
    </Box>
  );
}

function App() {
  const { isConnected } = useWebSocket();

  const animationStyle = `
    @keyframes scaleAnimation {
      0%, 100% {
        transform: scale(1);
      }
      50% {
        transform: scale(1.2);
      }
    }
  `;

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <ConnectorCreatorProvider>
        <Router>
          <DashboardErrorStatusProvider>
            <RouteAccessModeSync />
            <StatusBar />
            <SoftwareCanBeUpdated />
            <MfaSetupDialog />
            <CaseCreationOverlay />

            <Routes>
              {/* Login route - accessible without authentication */}
              <Route path="/login" element={<LoginPage />} />
              <Route path="/signup" element={<SaasAccessPage />} />
              <Route path="/verify-email" element={<SaasAccessPage />} />
              <Route path="/accept-invitation" element={<SaasAccessPage />} />

              {/* Public case route - accessible without authentication */}
              <Route path="/public/case/:caseid" element={
                <div style={{ display: 'flex', position: 'relative', width: '100%', maxWidth: '100vw', overflowX: 'hidden' }}>
                  <DisconnectedOverlay isConnected={isConnected} />
                  <Box flexGrow={1} sx={{ width: '100%', maxWidth: '100%', overflowX: 'hidden' }}>
                    <Dashboard />
                  </Box>
                </div>
              } />

              {/* Protected routes */}
              <Route path="/*" element={
                <AuthRoute>
                  <div style={{ display: 'flex', position: 'relative', width: '100%', maxWidth: '100vw', overflowX: 'hidden' }}>
                    <RememberProjectRoute />
                    <DisconnectedOverlay isConnected={isConnected} />

                    <SidebarForRoute />
                    <Box flexGrow={1} sx={{ width: '100%', maxWidth: '100%', overflowX: 'hidden' }}>
                      <style>
                        {animationStyle}
                      </style>
                      <OrganizationWorkspaceNavigation>{routeLocation => <Routes location={routeLocation}>
                        <Route path="/case/:caseid" element={<ValidatorCaseRoute />} />
                        <Route path="/add" element={
                          <ProtectedRoute>
                            <AddProjectRoute />
                          </ProtectedRoute>
                        } />
                        <Route path="/purchase-credits" element={
                          <PurchaseCreditsRoute />
                        } />
                        <Route path="/get" element={<GetCase />} />
                        <Route path="/welcome" element={<OrganizationWorkspace welcome />} />
                        <Route path="/platform" element={<SaasPlatformConsole />} />
                        <Route path="/Settings" element={
                          <ProtectedRoute allowReadOnly>
                            <GlobalSetting />
                          </ProtectedRoute>
                        } />
                        <Route path="/callback" element={<CallbackOutlookApiPage />} />
                        <Route path="/project-dashboard" element={<ValidatorProjectDashboardRoute />} />
                        <Route path="/validate" element={<ValidatorWorkspaceRoute />} />
                        <Route path="/validate/:caseid" element={<ValidatorWorkspaceRoute />} />
                        <Route path="/home-denied" element={<Navigate to="/project-dashboard" replace />} />
                        <Route path="/" element={<HomeRoute />} />
                      </Routes>}</OrganizationWorkspaceNavigation>
                    </Box>
                  </div>
                </AuthRoute>
              } />
            </Routes>
          </DashboardErrorStatusProvider>
        </Router>
      </ConnectorCreatorProvider>
    </LocalizationProvider>
  );
}

export default App;
